import { useEffect, useMemo, useRef, useState } from 'react';
import { Minus, Plus, Atom, BarChart3 } from 'lucide-react';
import { BY_Z } from '../elements';
import {
  groundStateFill,
  fillsToNotation,
  toSuperscript,
  stabilityHint,
  resolveElement,
} from './atomicData';
import { useAnimationFrame } from '../useAnimationFrame';
import {
  VizCard,
  NowYouTry,
  VIZ_FOCUS,
  VIZ_TOGGLE_SELECTED,
  cueIsActive,
  cueHighlights,
  usePrefersReducedMotion,
  type VizCue,
} from '../vizShared';

// AtomBuilderPro — an upgrade of BohrAtom for college gen-chem.
//   • build any atom/ion with ± protons / neutrons / electrons.
//   • TWO views: (a) Bohr shells with orbiting electrons, (b) an energy-level /
//     subshell diagram (1s, 2s, 2p … stacked by relative energy with occupancy).
//   • derives the element, isotope notation (A,Z,charge), ion charge, and a
//     stability hint from the neutron/proton ratio (band-of-stability heuristic).
//   • self-demo on mount: "assembles" the atom by ramping the displayed nucleon /
//     electron counts up to the configured values, then shows "Now you try".
//     Optional `cue` supersedes the demo: step/progress drive the assembly ramp,
//     highlight switches view ('bohr'/'energy') or picks an element by symbol.
// Resilient: defaults to neutral carbon; clamps every count to a valid range.

export interface AtomBuilderProConfig {
  title?: string;
  protons?: number;
  neutrons?: number;
  electrons?: number;
  view?: 'bohr' | 'energy';
}

interface Props {
  config?: AtomBuilderProConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const SIZE = 300;
const C = SIZE / 2;
const NUCLEUS_R = 30;

const SUB_COLOR: Record<string, string> = {
  s: '#5B8FF9',
  p: '#5AD8A6',
  d: '#F6BD16',
  f: '#E8684A',
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

// Shell capacities for the Bohr (2n²) teaching view.
const SHELL_CAP = [2, 8, 18, 32, 32, 18, 8];
function shellOccupancy(electrons: number): number[] {
  const shells: number[] = [];
  let remaining = Math.max(0, Math.floor(electrons));
  for (const cap of SHELL_CAP) {
    if (remaining <= 0) break;
    const inShell = Math.min(cap, remaining);
    shells.push(inShell);
    remaining -= inShell;
  }
  if (remaining > 0) shells.push(remaining);
  return shells;
}

export default function AtomBuilderPro({ config = {}, onComplete, cue }: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const [protons, setProtons] = useState(() => clamp(config.protons ?? 6, 1, 118));
  const [neutrons, setNeutrons] = useState(() =>
    clamp(config.neutrons ?? config.protons ?? 6, 0, 250),
  );
  const [electrons, setElectrons] = useState(() =>
    clamp(config.electrons ?? config.protons ?? 6, 0, 118),
  );
  const [view, setView] = useState<'bohr' | 'energy'>(config.view ?? 'bohr');
  const [angle, setAngle] = useState(0);
  const interactedRef = useRef(false);

  // ── Self-demo: ramp a 0..1 "assembly" fraction up over ~1.5s on mount ──
  const demoEnabled = !reducedMotion && !hasCue;
  // assembly in [0,1] scales the DISPLAYED counts; 1 = fully built.
  const [assembly, setAssembly] = useState(() => (demoEnabled ? 0 : 1));
  const [demoDone, setDemoDone] = useState(!demoEnabled);
  const demoActiveRef = useRef(demoEnabled);
  const demoStartRef = useRef(0);

  useAnimationFrame((dt) => setAngle((a) => a + dt * 0.8), view === 'bohr');

  // Drive the assembly ramp via rAF while the demo is active.
  useAnimationFrame((_dt, elapsed) => {
    if (!demoActiveRef.current) return;
    if (demoStartRef.current === 0) demoStartRef.current = elapsed;
    const t = clamp((elapsed - demoStartRef.current) / 1.5, 0, 1);
    setAssembly(t);
    if (t >= 1) {
      demoActiveRef.current = false;
      setDemoDone(true);
    }
  }, demoEnabled && !demoDone);

  const cancelDemo = () => {
    if (demoActiveRef.current) {
      demoActiveRef.current = false;
      setAssembly(1);
      setDemoDone(true);
    }
  };

  // ── Cue handling (supersedes self-demo) ──
  const cueTokens = useMemo(() => cueHighlights(cue), [cue]);
  // highlight: switch view, or pick an element by symbol/Z.
  const lastHighlight = useRef<string | null>(null);
  useEffect(() => {
    if (interactedRef.current) return; // user has taken manual control of the steppers
    const key = cueTokens.join(',');
    if (!key || key === lastHighlight.current) return;
    lastHighlight.current = key;
    cancelDemo();
    if (cueTokens.includes('energy')) setView('energy');
    else if (cueTokens.includes('bohr')) setView('bohr');
    // Resolve an element from any non-view token.
    for (const t of cueTokens) {
      if (t === 'energy' || t === 'bohr') continue;
      const resolved = resolveElement(t);
      if (resolved) {
        setProtons(clamp(resolved.z, 1, 118));
        setNeutrons(clamp(Math.round(resolved.mass) - resolved.z, 0, 250));
        setElectrons(clamp(resolved.z, 0, 118));
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cueTokens]);

  // step / progress drive the assembly ramp (0..1) — but ONLY until the user
  // takes manual control via the ± steppers (otherwise every audio tick would
  // re-scale and stomp their edits).
  useEffect(() => {
    if (interactedRef.current) return;
    // Animate the assembly ONLY while playing; at rest (paused) show the fully
    // built atom so the stage isn't an empty/partial shell.
    if (cue?.play === true && cue?.step != null) {
      cancelDemo();
      setAssembly(clamp(cue.step, 0, 1));
    } else if (cue?.play === true && cue?.progress != null) {
      cancelDemo();
      setAssembly(clamp(cue.progress, 0, 1));
    } else if (cue != null) {
      setAssembly(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.step, cue?.progress, cue?.play]);

  const markInteracted = () => {
    if (interactedRef.current) return;
    interactedRef.current = true;
    // User takes control: stop the narration-driven assembly ramp and pin the
    // atom to its real counts (1:1) so the ± steppers respond instead of being
    // continuously re-scaled by cue.progress.
    demoActiveRef.current = false;
    setAssembly(1);
    onComplete?.({ type: 'atom_builder', protons, neutrons, electrons });
  };

  const adjust = (
    setter: React.Dispatch<React.SetStateAction<number>>,
    delta: number,
    min: number,
    max: number,
  ) => {
    cancelDemo();
    setter((v) => clamp(v + delta, min, max));
    markInteracted();
  };

  const switchView = (v: 'bohr' | 'energy') => {
    cancelDemo();
    setView(v);
    markInteracted();
  };

  // Displayed counts during the assembly ramp (ceil so the first particle pops
  // in immediately and the last lands exactly on the target).
  const ramp = (n: number) => (assembly >= 1 ? n : Math.ceil(n * assembly));
  const dispProtons = ramp(protons);
  const dispNeutrons = ramp(neutrons);
  const dispElectrons = ramp(electrons);

  const el = BY_Z[protons];
  const massNumber = protons + neutrons;
  const charge = protons - electrons;
  const chargeLabel = charge === 0 ? '0' : charge > 0 ? `${charge}+` : `${-charge}-`;
  const shells = shellOccupancy(dispElectrons);
  const { fills } = groundStateFill(dispElectrons);
  const stab = stabilityHint(protons, neutrons);

  // Nucleon packing (phyllotaxis) — uses displayed counts so the demo fills in.
  const nucleons: Array<{ x: number; y: number; type: 'p' | 'n' }> = [];
  {
    const total = dispProtons + dispNeutrons;
    const typed: Array<'p' | 'n'> = [];
    let pLeft = dispProtons;
    let nLeft = dispNeutrons;
    for (let i = 0; i < total; i++) {
      if (i % 2 === 0 && pLeft > 0) { typed.push('p'); pLeft--; }
      else if (nLeft > 0) { typed.push('n'); nLeft--; }
      else if (pLeft > 0) { typed.push('p'); pLeft--; }
    }
    const golden = Math.PI * (3 - Math.sqrt(5));
    typed.forEach((t, i) => {
      const r = (NUCLEUS_R - 5) * Math.sqrt(i / Math.max(1, total));
      const a = i * golden;
      nucleons.push({ x: C + r * Math.cos(a), y: C + r * Math.sin(a), type: t });
    });
  }

  const stabColor =
    stab.level === 'stable'
      ? 'text-ws-success'
      : stab.level === 'likely'
        ? 'text-ws-warning'
        : 'text-ws-danger';

  const showNowYouTry = demoDone && demoEnabled && !hasCue;

  return (
    <VizCard
      title={config.title ?? 'Atom builder'}
      eyebrow="Build an atom"
      subtitle="± protons · neutrons · electrons · switch Bohr / energy view"
      hint="Adjust counts to form isotopes & ions · toggle the view for shells vs. subshells"
      reducedMotion={reducedMotion}
    >
      <div className="relative flex w-full flex-col gap-3 lg:flex-row">
        <NowYouTry show={showNowYouTry} />

        {/* Visualization */}
        <div className="flex flex-shrink-0 flex-col gap-2">
          {/* View toggle */}
          <div className="flex items-center gap-1 self-start rounded-full bg-ws-surface-2 p-0.5">
            <button
              type="button"
              onClick={() => switchView('bohr')}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${VIZ_FOCUS} ${
                view === 'bohr' ? VIZ_TOGGLE_SELECTED : 'text-ws-ink-2'
              }`}
            >
              <Atom size={12} /> Bohr
            </button>
            <button
              type="button"
              onClick={() => switchView('energy')}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${VIZ_FOCUS} ${
                view === 'energy' ? VIZ_TOGGLE_SELECTED : 'text-ws-ink-2'
              }`}
            >
              <BarChart3 size={12} /> Energy levels
            </button>
          </div>

          {view === 'bohr' ? (
            <svg
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className="w-full max-w-[300px]"
              role="img"
              aria-label={`Bohr model: ${el?.name ?? 'atom'}`}
            >
              {shells.map((count, s) => {
                const radius = NUCLEUS_R + 22 + s * 24;
                return (
                  <g key={s}>
                    <circle cx={C} cy={C} r={radius} fill="none" stroke="var(--ws-border)" strokeWidth="1" />
                    {Array.from({ length: count }).map((_, i) => {
                      const base = (i / count) * Math.PI * 2;
                      const a = base + angle / (1 + s * 0.4);
                      const ex = C + radius * Math.cos(a);
                      const ey = C + radius * Math.sin(a);
                      return <circle key={i} cx={ex} cy={ey} r="4.5" fill="#F2AF3F" stroke="#fff" strokeWidth="1" />;
                    })}
                  </g>
                );
              })}
              <circle cx={C} cy={C} r={NUCLEUS_R} fill="var(--ws-surface)" stroke="var(--ws-border-strong)" strokeWidth="1" />
              {nucleons.map((n, i) => (
                <circle
                  key={i}
                  cx={n.x}
                  cy={n.y}
                  r="4.5"
                  fill={n.type === 'p' ? '#E0564B' : '#3B82F6'}
                  stroke="rgba(0,0,0,0.2)"
                  strokeWidth="0.5"
                />
              ))}
              <text x={C} y={C + NUCLEUS_R + 14} textAnchor="middle" className="fill-ws-ink-3" style={{ fontSize: 9 }}>
                {shells.join(', ') || '0'}
              </text>
            </svg>
          ) : (
            <EnergyDiagram fills={fills} />
          )}
        </div>

        {/* Readout + controls */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          {/* Isotope notation: superscript A, subscript Z, charge */}
          <div className="flex items-center gap-3">
            <div className="flex items-stretch font-mono leading-none">
              <div className="mr-0.5 flex flex-col justify-center text-[11px] text-ws-ink-2">
                <span>{massNumber}</span>
                <span>{protons}</span>
              </div>
              <span className="self-center text-3xl font-bold text-ws-ink">{el?.symbol ?? '?'}</span>
              {charge !== 0 && (
                <span className="self-start text-sm font-semibold text-ws-ink-2">{chargeLabel}</span>
              )}
            </div>
            <div>
              <div className="text-sm font-medium text-ws-ink">{el?.name ?? 'Unknown'}</div>
              <div className="text-xs text-ws-ink-3">
                {charge === 0 ? 'Neutral atom' : charge > 0 ? `Cation (${chargeLabel})` : `Anion (${chargeLabel})`}
              </div>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-ws-ink-2">
            <div><dt className="inline text-ws-ink-3">Atomic no. (Z): </dt><dd className="inline font-medium">{protons}</dd></div>
            <div><dt className="inline text-ws-ink-3">Mass no. (A): </dt><dd className="inline font-medium">{massNumber}</dd></div>
            <div>
              <dt className="inline text-ws-ink-3">Charge: </dt>
              <dd className={`inline font-medium ${charge === 0 ? '' : charge > 0 ? 'text-ws-danger' : 'text-ws-accent'}`}>{chargeLabel}</dd>
            </div>
            <div className="truncate"><dt className="inline text-ws-ink-3">Config: </dt><dd className="inline font-mono font-medium">{fillsToNotation(groundStateFill(electrons).fills) || '—'}</dd></div>
          </dl>

          <div className={`rounded-md border border-ws-border bg-ws-surface-2 p-2 text-[11px] ${stabColor}`}>
            <span className="font-semibold uppercase tracking-wide">{stab.level}</span>
            <span className="ml-2 text-ws-ink-2">{stab.note}</span>
          </div>

          <div className="flex flex-col gap-2">
            <Stepper label="Protons" color="#E0564B" value={protons} onDec={() => adjust(setProtons, -1, 1, 118)} onInc={() => adjust(setProtons, 1, 1, 118)} />
            <Stepper label="Neutrons" color="#3B82F6" value={neutrons} onDec={() => adjust(setNeutrons, -1, 0, 250)} onInc={() => adjust(setNeutrons, 1, 0, 250)} />
            <Stepper label="Electrons" color="#F2AF3F" value={electrons} onDec={() => adjust(setElectrons, -1, 0, 118)} onInc={() => adjust(setElectrons, 1, 0, 118)} />
          </div>
        </div>
      </div>
    </VizCard>
  );
}

// Energy-level diagram: subshells stacked vertically by relative energy (aufbau
// order = increasing energy), each with its occupancy boxes.
function EnergyDiagram({ fills }: { fills: Array<{ n: number; sub: string; count: number; label: string }> }) {
  // Show in reverse (highest energy on top) for an intuitive ladder.
  const ladder = [...fills].reverse();
  return (
    <div className="flex w-[300px] max-w-full flex-col gap-1 rounded-md border border-ws-border bg-ws-surface p-3">
      <div className="mb-1 flex items-center justify-between text-[10px] text-ws-ink-3">
        <span>Energy</span>
        <span>occupancy</span>
      </div>
      {ladder.length === 0 && (
        <span className="py-4 text-center text-xs text-ws-ink-3">No electrons.</span>
      )}
      {ladder.map((f) => {
        const color = SUB_COLOR[f.sub] ?? '#94A3B8';
        const boxes = f.sub === 's' ? 1 : f.sub === 'p' ? 3 : f.sub === 'd' ? 5 : 7;
        // Fill boxes obeying Hund's rule: one spin-up each, then pair up.
        const slots = Array.from({ length: boxes }, () => ({ up: false, down: false }));
        let rem = f.count;
        for (let i = 0; i < boxes && rem > 0; i++) { slots[i].up = true; rem--; }
        for (let i = 0; i < boxes && rem > 0; i++) { slots[i].down = true; rem--; }
        return (
          <div key={f.label} className="flex items-center gap-2">
            <span className="w-7 font-mono text-[11px] text-ws-ink-2">{f.label}</span>
            <div className="flex gap-0.5">
              {slots.map((s, i) => (
                <div
                  key={i}
                  className="flex h-5 w-4 items-center justify-center rounded-[2px] border"
                  style={{ borderColor: color }}
                >
                  <span className="text-[9px] leading-none" style={{ color }}>
                    {s.up && s.down ? '⇅' : s.up ? '↑' : ''}
                  </span>
                </div>
              ))}
            </div>
            <span className="ml-auto font-mono text-[10px] text-ws-ink-3">
              {f.label}
              {toSuperscript(f.count)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Stepper({
  label,
  color,
  value,
  onDec,
  onInc,
}: {
  label: string;
  color: string;
  value: number;
  onDec: () => void;
  onInc: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="w-20 text-xs text-ws-ink-2">{label}</span>
      <div className="flex items-center overflow-hidden rounded-sm border border-ws-border">
        <button type="button" onClick={onDec} className={`px-2 py-1 text-ws-ink-2 transition-colors hover:bg-ws-surface-2 ${VIZ_FOCUS}`} aria-label={`Decrease ${label}`}>
          <Minus size={13} />
        </button>
        <span className="w-9 text-center text-sm font-semibold text-ws-ink tabular-nums">{value}</span>
        <button type="button" onClick={onInc} className={`px-2 py-1 text-ws-ink-2 transition-colors hover:bg-ws-surface-2 ${VIZ_FOCUS}`} aria-label={`Increase ${label}`}>
          <Plus size={13} />
        </button>
      </div>
    </div>
  );
}
